// src/pages/LiveBettingPage.jsx
import React, { useState, useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';

const LiveBettingPage = () => {
  const [liveEvents, setLiveEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Demo live events data
  const demoLiveEvents = [
    {
      id: 1,
      sport: 'football',
      homeTeam: 'Arsenal',
      awayTeam: 'Chelsea',
      score: '2-1',
      minute: '67',
      status: 'live',
      odds: {
        home: 2.10,
        draw: 3.40,
        away: 3.20
      },
      stats: {
        possession: '54% - 46%',
        shots: '12 - 8',
        corners: '5 - 3'
      }
    },
    {
      id: 2,
      sport: 'tennis',
      player1: 'Carlos Alcaraz',
      player2: 'Jannik Sinner',
      score: '6-4, 3-4',
      set: '2nd',
      status: 'live',
      odds: {
        player1: 1.80,
        player2: 2.00
      }
    },
    {
      id: 3,
      sport: 'basketball',
      homeTeam: 'Boston Celtics',
      awayTeam: 'Miami Heat',
      score: '98-95',
      quarter: '4th',
      timeRemaining: '2:45',
      status: 'live',
      odds: {
        home: 1.60,
        away: 2.30
      }
    },
    {
      id: 4,
      sport: 'esports',
      tournament: 'CS:GO Major',
      team1: 'NAVI',
      team2: 'FaZe Clan',
      mapScore: '1-0',
      currentMap: 'Mirage',
      status: 'live',
      odds: {
        team1: 2.50,
        team2: 1.50
      }
    }
  ];

  useEffect(() => {
    // Simulate live updates
    setLoading(true);
    
    const interval = setInterval(() => {
      // Update scores randomly for demo
      const updatedEvents = demoLiveEvents.map(event => ({
        ...event,
        odds: {
          home: (event.odds.home + (Math.random() * 0.1 - 0.05)).toFixed(2),
          away: (event.odds.away + (Math.random() * 0.1 - 0.05)).toFixed(2),
          draw: event.odds.draw ? (event.odds.draw + (Math.random() * 0.1 - 0.05)).toFixed(2) : undefined
        }
      }));
      setLiveEvents(updatedEvents);
    }, 3000);

    setTimeout(() => {
      setLiveEvents(demoLiveEvents);
      setLoading(false);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const filteredEvents = selectedCategory === 'all' 
    ? liveEvents 
    : liveEvents.filter(event => event.sport === selectedCategory);

  const addToBetslip = (event, selection, odds) => {
    const bet = {
      id: `${event.id}-${selection}-${Date.now()}`,
      match: `${event.homeTeam || event.player1 || event.team1} vs ${event.awayTeam || event.player2 || event.team2}`,
      selection,
      odds: parseFloat(odds),
      sport: event.sport,
      type: 'live'
    };
    
    const currentBets = JSON.parse(localStorage.getItem('betslip') || '[]');
    currentBets.push(bet);
    localStorage.setItem('betslip', JSON.stringify(currentBets));
    
    alert(`Live bet added to betslip!`);
  };

  return (
    <div className="min-h-screen bg-deep-black">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Live Betting Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
            <h1 className="text-3xl font-bold text-white">Live Betting</h1>
          </div>
          <p className="text-gray-400">Bet on ongoing matches with real-time odds updates</p>
        </div>

        {/* Live Stats Bar */}
        <div className="bg-gradient-to-r from-red-900/30 to-red-800/20 border border-red-700/30 rounded-xl p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-white">{liveEvents.length}</div>
                <div className="text-sm text-gray-300">Live Events</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-400">247</div>
                <div className="text-sm text-gray-300">Active Bets</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-400">$12.5k</div>
                <div className="text-sm text-gray-300">Live Volume</div>
              </div>
            </div>
            <div className="text-red-400 font-semibold animate-pulse">
              ⚡ LIVE UPDATES ACTIVE
            </div>
          </div>
        </div>

        {/* Category Filter */}
        <div className="flex space-x-2 mb-6 overflow-x-auto pb-2">
        {['all', 'football', 'basketball', 'tennis', 'esports', 'baseball'].map(category => (
          <button
            key={category}
            onClick={() => setSelectedCategory(category)}
            className={`px-4 py-2 rounded-lg whitespace-nowrap flex-shrink-0 ${
              selectedCategory === category
                ? 'bg-red-600 text-white'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            {category.charAt(0).toUpperCase() + category.slice(1)}
          </button>
        ))}
      </div>

        {/* Live Events Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500"></div>
            <p className="text-gray-400 mt-4">Loading live events...</p>
          </div>
        ) : filteredEvents.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-400">No live events in this category.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filteredEvents.map(event => (
              <div key={event.id} className="bg-gray-900 rounded-xl p-6 border border-red-700/30">
                {/* Event Header */}
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-1 bg-red-600 text-white text-xs rounded">
                        LIVE
                      </span>
                      <span className="text-gray-400 text-sm">
                        {event.sport.toUpperCase()}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-white">
                      {event.homeTeam && `${event.homeTeam} vs ${event.awayTeam}`}
                      {event.player1 && `${event.player1} vs ${event.player2}`}
                      {event.team1 && `${event.team1} vs ${event.team2}`}
                    </h3>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-white mb-1">{event.score}</div>
                    <div className="text-gray-400 text-sm">
                      {event.minute && `${event.minute}'`}
                      {event.quarter && `${event.quarter} Qtr`}
                      {event.set && `${event.set} Set`}
                      {event.currentMap && `• ${event.currentMap}`}
                    </div>
                  </div>
                </div>

                {/* Live Stats */}
                {event.stats && (
                  <div className="bg-gray-800 rounded-lg p-3 mb-4">
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div>
                        <div className="text-sm text-gray-400">Possession</div>
                        <div className="text-white">{event.stats.possession}</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-400">Shots</div>
                        <div className="text-white">{event.stats.shots}</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-400">Corners</div>
                        <div className="text-white">{event.stats.corners}</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Betting Odds */}
                <div className="grid grid-cols-3 gap-3">
                  {event.odds.home && (
                    <button
                      onClick={() => addToBetslip(event, 
                        event.homeTeam || event.player1 || event.team1, 
                        event.odds.home
                      )}
                      className="bg-gray-800 hover:bg-blue-600 p-3 rounded-lg text-center transition-colors"
                    >
                      <div className="text-gray-300 text-sm mb-1">
                        {event.homeTeam ? 'Home' : event.player1 ? 'Player 1' : 'Team 1'}
                      </div>
                      <div className="text-xl font-bold text-white">{event.odds.home}</div>
                    </button>
                  )}

                  {event.odds.draw && (
                    <button
                      onClick={() => addToBetslip(event, 'Draw', event.odds.draw)}
                      className="bg-gray-800 hover:bg-gray-700 p-3 rounded-lg text-center transition-colors"
                    >
                      <div className="text-gray-300 text-sm mb-1">Draw</div>
                      <div className="text-xl font-bold text-white">{event.odds.draw}</div>
                    </button>
                  )}

                  {event.odds.away && (
                    <button
                      onClick={() => addToBetslip(event, 
                        event.awayTeam || event.player2 || event.team2, 
                        event.odds.away
                      )}
                      className="bg-gray-800 hover:bg-red-600 p-3 rounded-lg text-center transition-colors"
                    >
                      <div className="text-gray-300 text-sm mb-1">
                        {event.awayTeam ? 'Away' : event.player2 ? 'Player 2' : 'Team 2'}
                      </div>
                      <div className="text-xl font-bold text-white">{event.odds.away}</div>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Live Updates Info */}
        <div className="mt-8 bg-gray-900 rounded-xl p-6">
          <h3 className="text-lg font-bold text-white mb-4">ℹ️ About Live Betting</h3>
          <div className="text-gray-300 space-y-2">
            <p>• Odds update in real-time based on match progression</p>
            <p>• Place bets while the match is ongoing</p>
            <p>• Higher risk, higher potential rewards</p>
            <p>• Bets are settled immediately after match completion</p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default LiveBettingPage;